/**
 * Enrollment experience for Mercury Create Recipient.
 * Docs: https://docs.mercury.com/reference/createrecipient
 *
 * US → ACH (`electronic` + ABA). Any other country → international wire
 * (`iban` + `swiftCode` + required `countrySpecific`).
 *
 * TEMPORARY — the whole international rail is parked behind
 * INTERNATIONAL_ENROLLMENT_ENABLED in target-account-enrollment.ts. This file is the
 * narrower per-country switch (SUPPORTED_ENROLLMENT_COUNTRIES). Empty means
 * every country in ENROLLMENT_COUNTRIES is allowed, once the rail is on.
 *
 * Edit SUPPORTED_ENROLLMENT_COUNTRIES to limit the country picker.
 */
import type { Address } from '../types/address.js';
import { isInternationalAddress } from '../types/address.js';
import { RemittanceMethod } from '../enums.js';
import type { EnrollTargetAccountInput } from '../types/remittance.js';

/** ISO 3166-1 alpha-2. Empty = all countries in the catalog. */
export const SUPPORTED_ENROLLMENT_COUNTRIES: readonly string[] = [];

export const PAKISTANI_LEGAL_ID_TYPES = [
  { value: 'CNIC', label: 'CNIC' },
  { value: 'SNIC', label: 'SNIC' },
  { value: 'Passport', label: 'Passport' },
  { value: 'NTN', label: 'NTN' },
] as const;

export const SWIFT_ACCOUNT_TYPES = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
] as const;

export interface EnrollmentFieldSpec {
  key: EnrollmentFieldKey;
  label: string;
  helpText?: string;
  required: boolean;
  kind?: 'text' | 'select';
  options?: ReadonlyArray<{ value: string; label: string }>;
}

export type EnrollmentFieldKey =
  | 'routing_number'
  | 'account_number'
  | 'electronic_account_type'
  | 'swift_code'
  | 'iban'
  | 'bsb_code'
  | 'bank_code'
  | 'transit_number'
  | 'legal_id'
  | 'legal_id_type'
  | 'swift_account_type'
  | 'ifsc_code'
  | 'local_routing_number'
  | 'inn'
  | 'branch_code';

export type EnrollmentRail = 'ach' | 'international_wire';

export interface EnrollmentExperience {
  rail: EnrollmentRail;
  countryCode: string;
  countryName: string;
  supported: boolean;
  usesIban: boolean;
  fields: EnrollmentFieldSpec[];
  unsupportedReason?: string;
}

/** Display names for the country picker. */
export const ENROLLMENT_COUNTRIES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'PT', name: 'Portugal' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'GR', name: 'Greece' },
  { code: 'IL', name: 'Israel' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'QA', name: 'Qatar' },
  { code: 'TR', name: 'Turkey' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'GH', name: 'Ghana' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SG', name: 'Singapore' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'IN', name: 'India' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'BD', name: 'Bangladesh' },
  { code: 'PH', name: 'Philippines' },
  { code: 'TH', name: 'Thailand' },
  { code: 'VN', name: 'Vietnam' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'PE', name: 'Peru' },
  { code: 'UY', name: 'Uruguay' },
  { code: 'PY', name: 'Paraguay' },
  { code: 'DO', name: 'Dominican Republic' },
  { code: 'HN', name: 'Honduras' },
  { code: 'GT', name: 'Guatemala' },
  { code: 'CR', name: 'Costa Rica' },
  { code: 'PA', name: 'Panama' },
  { code: 'KZ', name: 'Kazakhstan' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'EG', name: 'Egypt' },
  { code: 'MA', name: 'Morocco' },
];

/** Countries that typically do not use IBAN — Mercury still wants the account in `iban`. */
const NON_IBAN_COUNTRIES = new Set([
  'AU', 'CA', 'CN', 'HK', 'ID', 'IN', 'JP', 'KE', 'KR', 'MX', 'MY',
  'NG', 'NZ', 'PH', 'SG', 'TH', 'TW', 'US', 'VN', 'ZA',
]);

const SWIFT: EnrollmentFieldSpec = {
  key: 'swift_code',
  label: 'SWIFT / BIC',
  required: true,
  helpText: '8 or 11 characters',
};
const IBAN: EnrollmentFieldSpec = { key: 'iban', label: 'IBAN', required: true };
const ACCOUNT: EnrollmentFieldSpec = { key: 'account_number', label: 'Account number', required: true };
const LEGAL_ID: EnrollmentFieldSpec = { key: 'legal_id', label: 'Legal ID', required: true };

function ibanFields(extra: EnrollmentFieldSpec[] = []): EnrollmentFieldSpec[] {
  return [SWIFT, IBAN, ...extra];
}

function localAccountFields(extra: EnrollmentFieldSpec[]): EnrollmentFieldSpec[] {
  return [...extra, ACCOUNT, SWIFT];
}

/**
 * TEMPORARY — parked with INTERNATIONAL_ENROLLMENT_ENABLED.
 * One field set per country — not generic wire + extras stacked.
 * Local routing countries (CA, AU, IN, …) lead with their domestic identifiers.
 */
const COUNTRY_FIELDS: Record<string, EnrollmentFieldSpec[]> = {
  AU: localAccountFields([
    { key: 'bsb_code', label: 'BSB code', required: true, helpText: '6-digit bank/state/branch' },
  ]),
  CA: localAccountFields([
    { key: 'bank_code', label: 'Institution number', required: true, helpText: '3-digit bank code' },
    { key: 'transit_number', label: 'Transit number', required: true, helpText: '5-digit branch transit' },
  ]),
  IN: localAccountFields([
    { key: 'ifsc_code', label: 'IFSC code', required: true },
  ]),
  PH: localAccountFields([
    { key: 'local_routing_number', label: 'Routing number', required: true },
  ]),
  ZA: localAccountFields([
    { key: 'branch_code', label: 'Branch code', required: true },
  ]),
  BR: ibanFields([LEGAL_ID]),
  CL: ibanFields([LEGAL_ID]),
  CO: ibanFields([LEGAL_ID]),
  DO: ibanFields([
    {
      key: 'swift_account_type',
      label: 'Account type',
      required: true,
      kind: 'select',
      options: SWIFT_ACCOUNT_TYPES,
    },
    LEGAL_ID,
  ]),
  HN: ibanFields([
    {
      key: 'swift_account_type',
      label: 'Account type',
      required: true,
      kind: 'select',
      options: SWIFT_ACCOUNT_TYPES,
    },
    LEGAL_ID,
  ]),
  KZ: ibanFields([LEGAL_ID]),
  PK: ibanFields([
    {
      key: 'legal_id_type',
      label: 'Legal ID type',
      required: true,
      kind: 'select',
      options: PAKISTANI_LEGAL_ID_TYPES,
    },
    LEGAL_ID,
  ]),
  PY: ibanFields([LEGAL_ID]),
  RU: ibanFields([
    { key: 'inn', label: 'INN', required: true, helpText: 'Tax identification number' },
  ]),
};

const MERCURY_COUNTRY_KEY: Record<string, string> = {
  AU: 'australia',
  BR: 'brazil',
  CA: 'canada',
  CL: 'chile',
  CO: 'colombia',
  DO: 'dominicanRepublic',
  HN: 'honduras',
  IN: 'india',
  KZ: 'kazakhstan',
  PK: 'pakistan',
  PY: 'paraguay',
  PH: 'philippines',
  RU: 'russia',
  ZA: 'southAfrica',
};

const COUNTRY_BY_CODE = new Map(ENROLLMENT_COUNTRIES.map((c) => [c.code, c]));

const COUNTRY_ALIASES: Record<string, string> = {
  usa: 'US',
  'u.s.': 'US',
  'u.s.a.': 'US',
  'united states': 'US',
  'united states of america': 'US',
  uk: 'GB',
  'great britain': 'GB',
  'united kingdom': 'GB',
  england: 'GB',
};

for (const country of ENROLLMENT_COUNTRIES) {
  COUNTRY_ALIASES[country.name.toLowerCase()] = country.code;
}

const ACH_FIELDS: EnrollmentFieldSpec[] = [
  { key: 'electronic_account_type', label: 'Account type', required: false, kind: 'select' },
  { key: 'routing_number', label: 'Routing number', required: true, helpText: '9-digit ABA routing number' },
  { key: 'account_number', label: 'Account number', required: true, helpText: '4–17 digits' },
];

function defaultWireFields(usesIban: boolean): EnrollmentFieldSpec[] {
  return usesIban ? ibanFields() : localAccountFields([]);
}

export function normalizeCountryCode(country: string | null | undefined): string {
  const raw = country?.trim() ?? '';
  if (!raw) return '';
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return COUNTRY_ALIASES[raw.toLowerCase()] ?? raw.toUpperCase();
}

export function countryName(code: string): string {
  return COUNTRY_BY_CODE.get(code)?.name ?? code;
}

export function isEnrollmentCountrySupported(country: string): boolean {
  const code = normalizeCountryCode(country);
  if (!code) return false;
  if (!COUNTRY_BY_CODE.has(code)) return SUPPORTED_ENROLLMENT_COUNTRIES.length === 0;
  if (SUPPORTED_ENROLLMENT_COUNTRIES.length === 0) return true;
  return SUPPORTED_ENROLLMENT_COUNTRIES.includes(code);
}

export function enrollmentCountryOptions(international: boolean): Array<{ code: string; name: string }> {
  const allow = SUPPORTED_ENROLLMENT_COUNTRIES.length > 0
    ? new Set(SUPPORTED_ENROLLMENT_COUNTRIES)
    : null;
  return ENROLLMENT_COUNTRIES.filter((c) => {
    if (allow && !allow.has(c.code)) return false;
    if (international && c.code === 'US') return false;
    if (!international && c.code !== 'US') return false;
    return true;
  });
}

export function remittanceMethodForDestination(international: boolean): RemittanceMethod {
  return international ? RemittanceMethod.Wire : RemittanceMethod.Ach;
}

export function enrollmentExperienceFor(
  address: Pick<Address, 'country'> | null | undefined,
  international: boolean,
): EnrollmentExperience {
  if (!international) {
    return {
      rail: 'ach',
      countryCode: 'US',
      countryName: 'United States',
      supported: true,
      usesIban: false,
      fields: ACH_FIELDS,
    };
  }

  const countryCode = normalizeCountryCode(address?.country);
  if (!countryCode || countryCode === 'US') {
    return {
      rail: 'international_wire',
      countryCode,
      countryName: countryCode ? countryName(countryCode) : '',
      supported: false,
      usesIban: true,
      fields: defaultWireFields(true),
      unsupportedReason: countryCode === 'US'
        ? 'International wire requires a non-US country'
        : 'Select a country',
    };
  }

  if (!isEnrollmentCountrySupported(countryCode)) {
    return {
      rail: 'international_wire',
      countryCode,
      countryName: countryName(countryCode),
      supported: false,
      usesIban: !NON_IBAN_COUNTRIES.has(countryCode),
      fields: [],
      unsupportedReason: `${countryName(countryCode)} is not a supported destination`,
    };
  }

  const fields = COUNTRY_FIELDS[countryCode] ?? defaultWireFields(!NON_IBAN_COUNTRIES.has(countryCode));
  return {
    rail: 'international_wire',
    countryCode,
    countryName: countryName(countryCode),
    supported: true,
    usesIban: fields.some((field) => field.key === 'iban'),
    fields,
  };
}

/** TEMPORARY — parked with INTERNATIONAL_ENROLLMENT_ENABLED. Mercury countrySpecific payload. */
export function mercuryCountrySpecific(
  countryCode: string,
  input: Partial<EnrollTargetAccountInput>,
): Record<string, unknown> {
  const extra = input.country_specific ?? {};
  const key = MERCURY_COUNTRY_KEY[countryCode];
  if (!key) return {};
  if (countryCode === 'AU') return { australia: { bsbCode: extra.bsb_code } };
  if (countryCode === 'BR') return { brazil: { legalId: extra.legal_id } };
  if (countryCode === 'CA') {
    return { canada: { bankCode: extra.bank_code, transitNumber: extra.transit_number } };
  }
  if (countryCode === 'CL') return { chile: { legalId: extra.legal_id } };
  if (countryCode === 'CO') return { colombia: { legalId: extra.legal_id } };
  if (countryCode === 'DO') {
    return { dominicanRepublic: { accountType: extra.swift_account_type, legalId: extra.legal_id } };
  }
  if (countryCode === 'HN') {
    return { honduras: { accountType: extra.swift_account_type, legalId: extra.legal_id } };
  }
  if (countryCode === 'IN') return { india: { ifscCode: extra.ifsc_code } };
  if (countryCode === 'KZ') return { kazakhstan: { legalId: extra.legal_id } };
  if (countryCode === 'PK') {
    return { pakistan: { legalId: extra.legal_id, legalIdType: extra.legal_id_type } };
  }
  if (countryCode === 'PY') return { paraguay: { legalId: extra.legal_id } };
  if (countryCode === 'PH') return { philippines: { routingNumber: extra.routing_number } };
  if (countryCode === 'RU') return { russia: { inn: extra.inn } };
  if (countryCode === 'ZA') return { southAfrica: { branchCode: extra.branch_code } };
  return {};
}

export function countrySpecificFromValues(values: Record<string, string>): EnrollTargetAccountInput['country_specific'] {
  return {
    bsb_code: values.bsb_code || undefined,
    bank_code: values.bank_code || undefined,
    transit_number: values.transit_number || undefined,
    legal_id: values.legal_id || undefined,
    legal_id_type: values.legal_id_type || undefined,
    swift_account_type: values.swift_account_type || undefined,
    ifsc_code: values.ifsc_code || undefined,
    routing_number: values.local_routing_number || undefined,
    inn: values.inn || undefined,
    branch_code: values.branch_code || undefined,
  };
}

export function isUsDestination(address: Pick<Address, 'country'> | null | undefined): boolean {
  return !isInternationalAddress(address);
}
